import { resetPasswordPayloadSchema } from '../../api/AuthModel'
import { FormErrorsAlert } from '@/shared/components'
import { useResetPassword, errors as apiErrors } from '../../hooks/useAuth'
import { Alert, Button, InputGroup } from '@/common'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { ZodIssue } from 'zod'
import AuthLayout from '../../components/AuthLayout'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [success, setSuccess] = useState(false)

  const [isInvalidToken, setInvalidToken] = useState(false)
  const [isExpiredToken, setExpiredToken] = useState(false)
  const [isAlreadyUsedToken, setAlreadyUsedToken] = useState(false)

  const { mutateAsync: resetPassword, isPending } = useResetPassword()

  const resetStatus = useCallback(() => {
    setErrors([])
    setInvalidToken(false)
    setExpiredToken(false)
    setAlreadyUsedToken(false)
    setSuccess(false)
  }, [])

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      resetStatus()

      const validationResult = resetPasswordPayloadSchema.safeParse({
        token: token,
        ...form
      })

      if (!validationResult.success) {
        setErrors(validationResult.error.issues)
      } else {
        try {
          await resetPassword(validationResult.data)
          setSuccess(true)
          toast.success(t('resetPassword.notifications.success'))
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === apiErrors.INVALID_PASSWORD_RESET_TOKEN) {
              setInvalidToken(true)
              return
            }
            if (error.message === apiErrors.PASSWORD_RESET_TOKEN_EXPIRED) {
              setExpiredToken(true)
              return
            }
            if (error.message === apiErrors.PASSWORD_RESET_TOKEN_ALREADY_USED) {
              setAlreadyUsedToken(true)
              return
            }
          }
          toast.error(t('resetPassword.notifications.error'))
        }
      }
    },
    [form, resetPassword, t, token]
  )

  const isSubmitEnabled =
    !success && token && form.password.length > 0 && form.confirmPassword.length > 0

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 w-full max-w-md">
        <div className="text-center">
          <p>{t('resetPassword.description')}</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 w-full mb-8">
          {(!token || isInvalidToken || isExpiredToken || isAlreadyUsedToken) && (
            <Alert type="error" className="whitespace-pre-line">
              {(!token || isInvalidToken) && t('resetPassword.errors.invalidToken')}
              {isExpiredToken && t('resetPassword.errors.expiredToken')}
              {isAlreadyUsedToken && (
                <Trans
                  i18nKey="resetPassword.errors.alreadyUsedToken"
                  components={{
                    Link: <Link className="text-blue-500 underline" to="/forgot-password" />
                  }}
                />
              )}
            </Alert>
          )}
          {success && (
            <Alert type="success" className="whitespace-pre-line">
              <Trans
                i18nKey="resetPassword.success"
                components={{
                  Link: <Link className="text-blue-500 underline" to="/login" />
                }}
              />
            </Alert>
          )}
          <FormErrorsAlert
            errors={errors}
            stringKeysPrefix="resetPassword.fields"
            useZodMessageField
          />
          <InputGroup
            onChange={evt => setForm({ ...form, password: evt.target.value })}
            value={form.password}
            type="password"
            disabled={success || !token}
            name="password"
            label={t('resetPassword.fields.password.label')}
          />
          <InputGroup
            onChange={evt => setForm({ ...form, confirmPassword: evt.target.value })}
            value={form.confirmPassword}
            type="password"
            disabled={success || !token}
            name="confirmPassword"
            label={t('resetPassword.fields.confirmPassword.label')}
          />
          <Button
            type="submit"
            className="w-full"
            isLoading={isPending}
            disabled={!isSubmitEnabled}
          >
            {t('resetPassword.actions.submit')}
          </Button>
        </form>
      </div>
    </AuthLayout>
  )
}
